import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { auth0_audience } from '../app.config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, user, logout } = useAuth0();
  const [token, setToken] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);

  const isTokenValid = (token) => {
    try {
      const { exp } = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      return exp > now;
    } catch (e) {
      return false;
    }
  };

  const loadToken = useCallback(async () => {
    try {
      const newToken = await getAccessTokenSilently();

      if (!isTokenValid(newToken)) {
        throw new Error('Token expired or invalid');
      }

      setToken(newToken);
      setAuthenticated(true);
    } catch (error) {
      setToken(null);
      setAuthenticated(false);
    }
  }, [getAccessTokenSilently, logout]);

  useEffect(() => {
    let interval;

    if (isAuthenticated) {
      loadToken();

      interval = setInterval(() => {
        loadToken();
      }, 900000);
    } else {
      setToken(null);
      setAuthenticated(false);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated, loadToken]);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        token,
        user,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
